import { Injectable } from '@angular/core';
import DailyIframe from '@daily-co/daily-js';
import { BehaviorSubject, Subject } from 'rxjs';
import { ParticipantService } from '../participant/participant.service';

@Injectable({
    providedIn: 'root',
})
export class ScreenShareService {
    /**
     * BehaviorSubject to check if the screen is being shared
     *  - Set in the [checkForScreenShare()]{@link ScreenShareService#checkForScreenShare} function
     *  - Used to set the [participantInViewMax]{@link ParticipantService#participantInViewMax} getter in the ParticipantService
     *  - Used to set the [isSharingScreen]{@link RoomComponent#isSharingScreen} getter in the RoomComponent
     *  - Used to set the [shareScreenEnabled]{@link RoomControlsComponent#shareScreenEnabled} getter in the RoomControlsComponent
     * - Used to set the [isSharingScreen]{@link RoomControlsComponent#isSharingScreen} getter in the RoomControlsComponent
     *  - Subscribed to in [setupEvents()]{@link RoomComponent#setupEvents} in the RoomComponent
     * - Subscribed to in [setupEvents()]{@link RoomControlsComponent#setupEvents} in the RoomControlsComponent
     */
    isSharingScreen = new BehaviorSubject(false);

    hasScreenShareTimer = new BehaviorSubject(false);

    /**
     * Gets the call instance from the DailyIframe
     *  - Used in [startScreenShare()]{@link ScreenShareService#startScreenShare}
     *  - Used in [stopScreenShare()]{@link ScreenShareService#stopScreenShare}
     */
    public get callObject(): any {
        return DailyIframe.getCallInstance();
    }

    constructor() {}

    /**
     * Starts the screen share, using the [callObject]{@link ScreenShareService#callObject}
     *  - Called in [setupTask()]{@link RoomComponent#setupTask} in the RoomComponent
     *  - Called in the [toggleShareScreen()]{@link RoomControlsComponent#toggleShareScreen} function in the RoomControlsComponent
     *  - Called in the [handleScreenShareStarted()]{@link StreamProviderService#handleScreenShareStarted} function in the StreamProviderService
     */
    startScreenShare() {
        this.callObject.startScreenShare({
            displayMediaOptions: {
                audio: true,
                selfBrowserSurface: 'exclude',
                systemAudio: 'include',
            },
            screenVideoSendSettings: 'motion-and-detail-balanced',
        });
    }

    /**
     * Stops the screen share, using the [callObject]{@link ScreenShareService#callObject}
     *  - Called in the [toggleShareScreen()]{@link RoomControlsComponent#toggleShareScreen} function in the RoomControlsComponent
     */
    stopScreenShare() {
        this.callObject.stopScreenShare();
    }

    /**
     * Checks if the screen is being shared
     *  - If the screen is being shared, set the [isSharingScreen]{@link ScreenShareService#isSharingScreen} BehaviorSubject to true otherwise set it to false
     *  - Called in [handleParticipantLeft()]{@link StreamProviderService#handleParticipantLeft} in the StreamProviderService
     * - Called in [handleParticipantUpdated()]{@link StreamProviderService#handleParticipantUpdated} in the StreamProviderService
     * @param screenShareCount - the number of screen shares
     */
    checkForScreenShare(screenShareCount: number) {
        if (screenShareCount > 0) {
            this.isSharingScreen.next(true);
        } else {
            this.isSharingScreen.next(false);
        }
    }
}
